'use client';

import { useEffect, useState } from 'react';
import { ORIGIN_KEY } from '@/lib/user-preferences';
import { detectGeoCountry, GEO_CACHE_KEY } from '@/hooks/useAutoCountry';

interface TodayGeoContextProps {
  utcDate: string;
  children: React.ReactNode;
}

/**
 * Client wrapper for the /today page.
 * 1. Visually spotlights user's country with 3-layer CSS injection
 * 2. Shows local-date mismatch notice
 */
export function TodayGeoContext({ utcDate, children }: TodayGeoContextProps) {
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [localDate, setLocalDate] = useState<string | null>(null);

  useEffect(() => {
    // Detect user's country from stored preference or geolocation cache
    const stored = localStorage.getItem(ORIGIN_KEY);
    if (stored) {
      setUserCountry(stored);
    } else {
      const cached = localStorage.getItem(GEO_CACHE_KEY);
      if (cached) {
        try {
          const { code } = JSON.parse(cached);
          if (code) setUserCountry(code);
        } catch { /* ignore */ }
      } else {
        detectGeoCountry().then(code => {
          if (code) setUserCountry(code);
        });
      }
    }

    // Check if local date differs from server UTC date
    const now = new Date();
    const userLocalDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (userLocalDate !== utcDate) {
      setLocalDate(
        new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(now),
      );
    }
  }, [utcDate]);

  return (
    <div data-user-country={userCountry ?? undefined}>
      {localDate && (
        <div className="mx-auto mb-6 max-w-6xl rounded-lg border panel-warning px-4 py-3 text-sm text-text-primary">
          It&apos;s already <strong>{localDate}</strong> where you are. Showing holidays for{' '}
          <strong>{utcDate}</strong> (UTC).
        </div>
      )}

      {userCountry && (
        <style dangerouslySetInnerHTML={{ __html: `
          /* Hide user's country card in regions — shown in spotlight above */
          [data-user-country="${userCountry}"] [data-country-section="${userCountry}"] {
            display: none;
          }
        ` }} />
      )}

      {children}
    </div>
  );
}
